import { useTranslation } from "react-i18next";

import {
  missionRewardGames,
  missionRewardPrize,
} from "@/campaigns/modules/missions/types";
import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import { hasWon } from "@/campaigns/modules/sprint-mission/types";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { MissionTaskRow } from "@/components/campaigns/missions/mission-task-row";
import {
  taskVariant,
  taskVariantInputs,
} from "@/components/campaigns/missions/task-variant";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

import { useSprintMissionPhase } from "./use-sprint-mission-phase";
import { PrizeText } from "@/components/prize-text";

/**
 * The mission card — the one prize and the tasks that win it.
 *
 * Sits under the winners card. While the run is live the head reads "Complete the mission to
 * win", with "Final task" on the right once only the active task is left; pool urgency only
 * colours the winners card above — the two signals are independent on purpose. A winner sees
 * "Mission completed" over their tasks, all done (the prize moved up into the status card); a
 * finished run the dimmed prize and tasks with no head. The prize strip carries the single mission prize
 * (the subtype guarantees one mission, one prize, no task prizes); its title is never truncated
 * (CLAUDE.md), it wraps. Task rows reuse `MissionTaskRow` + `taskVariant` so a task reads
 * identically to the standard ladder.
 */
export const SprintMissionMissionCard = ({
  sprintMission,
  density = "default",
  className,
}: {
  sprintMission: SprintMission;
  /** `dock`: the overlay's narrow column — "One time prize" moves above the prize as an
   *  overline, so the prize keeps the strip's whole width; the task rows follow suit (see
   *  `MissionTaskRow`). */
  density?: "default" | "dock";
  className?: string;
}) => {
  const { t } = useTranslation();
  const phase = useSprintMissionPhase(sprintMission);
  const mission = sprintMission.mission;

  if (!mission || mission.tasks.length === 0) return null;

  const won = hasWon(sprintMission);
  const finished = phase === "finished";
  const crucial = phase === "crucial";
  const { activeCount, hasLiveState } = taskVariantInputs(mission.tasks);
  const remainingTasks = mission.tasks.filter((task) => !task.isCompleted);
  // "Final task" needs live state: before the progress stream lands every task looks open.
  const finalTask =
    !won &&
    !finished &&
    hasLiveState &&
    remainingTasks.length === 1 &&
    remainingTasks[0].isActive;

  const prize = missionRewardPrize(mission);
  const prizeTitle = prize ? formatPrizeTitle(prize.title, t) : null;

  const live = !won && !finished;

  return (
    <Card
      data-phase={phase}
      // A winner keeps their task list (QA: "show the tasks the player completed"); the head
      // says the mission is done instead of the live caption. Finished: no head at all.
      title={
        won && !finished
          ? t("sprintMission.missionCompleted", "Mission completed")
          : live
            ? t(
                "sprintMission.completeTheMissionToWin",
                "Complete the mission to win"
              )
            : undefined
      }
      aside={
        live && finalTask ? (
          <Badge
            variant="text"
            color={crucial ? "urgent" : "primary"}
            className="font-semibold tracking-[0.88px] uppercase"
          >
            {t("sprintMission.finalTask", "Final task")}
          </Badge>
        ) : undefined
      }
      titleRowClassName={cn("mb-0", won && !finished && "[&>h3]:text-active")}
      className={cn("flex flex-col gap-4", className)}
    >
      {prizeTitle && !won && (
        <div
          className={cn(
            "flex w-full flex-col gap-1.5 rounded-(--border-radius-inner) border border-prizes/30 bg-background-card px-4 py-3",
            finished && FINISHED_DIM
          )}
        >
          <div
            className={cn(
              "flex w-full gap-4",
              density === "dock"
                ? "flex-col-reverse gap-1"
                : "items-center justify-between"
            )}
          >
            {/* Never truncated: the prize is the card's priority content (CLAUDE.md) — it
                shrinks, then glides on its one row. */}
            <PrizeText size="title" icon fit="marquee" className="min-w-0">
              {prizeTitle}
            </PrizeText>
            <span className="shrink-0 font-heading text-[12px] leading-4 font-bold tracking-[0.5px] text-prizes uppercase">
              {t("sprintMission.oneTimePrize", "One time prize")}
            </span>
          </div>
          {/* The games on their own line under the prize, so the prize keeps its whole row. */}
          <PrizeGamesChips games={missionRewardGames(mission)} />
        </div>
      )}

      <div className={cn("flex w-full flex-col", finished && FINISHED_DIM)}>
        {mission.tasks.map((task) => (
          <MissionTaskRow
            key={task.id}
            task={task}
            // Every task of a winner is done by definition; the live variants otherwise.
            variant={
              won ? "done" : taskVariant(task, activeCount, hasLiveState)
            }
            // A sprint mission has one rung and it is always the live one, so a turnover
            // task here may carry the contribution caveat — except for a winner, who has
            // nothing left to progress.
            showContributionNote={!won && !finished}
            density={density}
          />
        ))}
      </div>
    </Card>
  );
};
